// The apps in the signed-in person's saved Focus, for the command palette.
// Read only once the palette opens, and cached briefly because AppLayout
// remounts on every page.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { focusAppKeys, resolveFocus } from './landingFocus';
import { fetchPreferences } from './userPreferences';

const TTL_MS = 60_000;
const cache = new Map<string, { at: number; keys: ReadonlySet<string> | null }>();

/** Call after the person changes their focus. */
export function invalidateFocusAppKeys(): void {
  cache.clear();
}

export function useFocusAppKeys(enabled: boolean): ReadonlySet<string> | null {
  const { session } = useAuth();
  const uid = session?.user.uid ?? null;
  const [keys, setKeys] = useState<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (!enabled || !session || !uid) return;
    const hit = cache.get(uid);
    if (hit && Date.now() - hit.at < TTL_MS) {
      setKeys(hit.keys);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchPreferences(await session.user.getIdToken());
        const next = focusAppKeys(resolveFocus(res.focus?.areas));
        cache.set(uid, { at: Date.now(), keys: next });
        if (!cancelled) setKeys(next);
      } catch {
        // No focus just means the palette keeps its normal order.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, session, uid]);

  return keys;
}
